import React from 'react';
import { Link } from 'react-router-dom';

const DataProtection: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-lg shadow-sm p-8">
          <div className="mb-8">
            <Link to="/" className="text-blue-600 hover:text-blue-800 text-sm font-medium">
              ← Back to Home
            </Link>
          </div>
          
          <h1 className="text-3xl font-bold text-gray-900 mb-8">Data Protection</h1>
          <p className="text-gray-600 mb-6">Last updated: {new Date().toLocaleDateString()}</p>

          <div className="prose prose-lg max-w-none">
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Our Commitment to Data Protection</h2>
              <p className="text-gray-700 mb-4">
                Flash AI protects personal data and is designed to follow applicable data protection laws,
                including the General Data Protection Regulation (GDPR) and other privacy regulations.
                This page is not a certification.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Data Controller Information</h2>
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <p className="text-gray-700">
                  <strong>Data Controller:</strong> Flash AI<br />
                  <strong>Address:</strong> 123 Business Street, Suite 100, City, State 12345<br />
                  <strong>Email:</strong> privacy@flashai.com<br />
                  <strong>Phone:</strong> +1 (555) 123-4567
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. Legal Basis for Processing</h2>
              <p className="text-gray-700 mb-4">We process your personal data based on the following legal grounds:</p>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3">3.1 Contract Performance</h3>
              <p className="text-gray-700 mb-4">
                We process your data to provide our services under the terms of our agreement with you.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">3.2 Legitimate Interest</h3>
              <p className="text-gray-700 mb-4">
                We process data to improve our services, ensure security, and conduct business operations.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">3.3 Consent</h3>
              <p className="text-gray-700 mb-4">
                We process certain data based on your explicit consent, which you can withdraw at any time.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">3.4 Legal Obligation</h3>
              <p className="text-gray-700 mb-4">
                We may process data to comply with applicable laws and regulations.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Your Data Protection Rights</h2>
              <p className="text-gray-700 mb-4">Under GDPR and other applicable laws, you have the following rights:</p>
              
              <div className="space-y-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-blue-900 mb-2">Right of Access</h3>
                  <p className="text-blue-800">
                    You can request a copy of the personal data we hold about you and information about how we process it.
                  </p>
                </div>

                <div className="bg-green-50 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-green-900 mb-2">Right to Rectification</h3>
                  <p className="text-green-800">
                    You can request that we correct any inaccurate or incomplete personal data.
                  </p>
                </div>

                <div className="bg-red-50 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-red-900 mb-2">Right to Erasure</h3>
                  <p className="text-red-800">
                    You can request that we delete your personal data in certain circumstances.
                  </p>
                </div>

                <div className="bg-yellow-50 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-yellow-900 mb-2">Right to Restrict Processing</h3>
                  <p className="text-yellow-800">
                    You can request that we limit how we process your personal data.
                  </p>
                </div>

                <div className="bg-purple-50 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-purple-900 mb-2">Right to Data Portability</h3>
                  <p className="text-purple-800">
                    You can request a copy of your data in a structured, machine-readable format.
                  </p>
                </div>

                <div className="bg-indigo-50 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-indigo-900 mb-2">Right to Object</h3>
                  <p className="text-indigo-800">
                    You can object to certain types of processing, including direct marketing.
                  </p>
                </div>
              </div>

              <div className="mt-6 bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Exercising your rights</h3>
                <p className="text-gray-700 mb-3">
                  Signed-in client accounts can download a copy of their data or request account deletion from{' '}
                  <Link to="/profile" className="text-blue-600 hover:text-blue-800 font-medium">
                    Profile → Data &amp; Privacy
                  </Link>
                  . Admin and insurer accounts should contact{' '}
                  <a href="mailto:privacy@flashai.com" className="text-blue-600 hover:text-blue-800">
                    privacy@flashai.com
                  </a>
                  .
                </p>
                <p className="text-sm text-gray-600">
                  API endpoints (authenticated): <code className="text-xs bg-white px-1 py-0.5 rounded">GET /v1/users/me/export</code>{' '}
                  and <code className="text-xs bg-white px-1 py-0.5 rounded">DELETE /v1/users/me</code>.
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Data Security Measures</h2>
              <p className="text-gray-700 mb-4">We implement comprehensive security measures to protect your data:</p>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3">5.1 Technical Safeguards</h3>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>TLS encryption for data in transit; at-rest encryption provided by our cloud infrastructure providers</li>
                <li>TOTP-based multi-factor authentication with platform-wide and per-insurer mandatory or optional policies</li>
                <li>Role-based access controls, account lockout after repeated failed sign-in attempts, and authenticated API access</li>
                <li>Regular security audits and penetration testing</li>
                <li>Secure data centers with physical access controls</li>
                <li>Automated backup and disaster recovery systems</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">5.2 Organizational Safeguards</h3>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Data protection training for all employees</li>
                <li>Access controls based on job responsibilities</li>
                <li>Regular security awareness programs</li>
                <li>Incident response procedures</li>
                <li>Data protection impact assessments</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Data Retention</h2>
              <p className="text-gray-700 mb-4">
                We retain your personal data only for as long as necessary to fulfill the purposes 
                outlined in our Privacy Policy:
              </p>
              
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Retention Periods</h3>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li><strong>Account Data:</strong> Until account deletion + 30 days</li>
                  <li><strong>Business Data:</strong> Until account deletion + 90 days</li>
                  <li><strong>Payment Records:</strong> 7 years (legal requirement)</li>
                  <li><strong>Support Communications:</strong> 3 years</li>
                  <li><strong>Analytics Data:</strong> 26 months (anonymized)</li>
                </ul>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. International Data Transfers</h2>
              <p className="text-gray-700 mb-4">
                When we transfer your data outside the European Economic Area (EEA), we ensure 
                appropriate safeguards are in place:
              </p>
              
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Adequacy decisions by the European Commission</li>
                <li>Standard Contractual Clauses (SCCs)</li>
                <li>Binding Corporate Rules</li>
                <li>Certification schemes and codes of conduct</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Data Breach Notification</h2>
              <p className="text-gray-700 mb-4">
                In the event of a data breach that poses a risk to your rights and freedoms, we will:
              </p>
              
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Notify the relevant supervisory authority within 72 hours</li>
                <li>Inform affected individuals without undue delay</li>
                <li>Provide clear information about the breach and its consequences</li>
                <li>Explain the measures taken to address the breach</li>
                <li>Offer guidance on steps you can take to protect yourself</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Exercising Your Rights</h2>
              <p className="text-gray-700 mb-4">
                To exercise any of your data protection rights, please contact us using the information below. 
                We will respond to your request within one month of receipt.
              </p>
              
              <div className="bg-blue-50 p-4 rounded-lg mb-4">
                <h3 className="text-lg font-semibold text-blue-900 mb-2">How to Contact Us</h3>
                <p className="text-blue-800">
                  <strong>Email:</strong> privacy@flashai.com<br />
                  <strong>Subject Line:</strong> "Data Protection Request"<br />
                  <strong>Include:</strong> Your full name, email address, and specific request
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Supervisory Authority</h2>
              <p className="text-gray-700 mb-4">
                If you are not satisfied with our response to your data protection concerns, you have 
                the right to lodge a complaint with your local supervisory authority.
              </p>
              
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">EU Residents</h3>
                <p className="text-gray-700">
                  Find your local supervisory authority at: 
                  <a href="https://edpb.europa.eu/about-edpb/board/members_en" className="text-blue-600 hover:underline ml-1">
                    European Data Protection Board
                  </a>
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Updates to This Policy</h2>
              <p className="text-gray-700 mb-4">
                We may update this Data Protection Policy from time to time to reflect changes in our 
                practices or applicable laws. We will notify you of any material changes and obtain 
                your consent where required.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">12. Contact Information</h2>
              <p className="text-gray-700 mb-4">
                For any questions about data protection or to exercise your rights, please contact us:
              </p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-gray-700">
                  <strong>Data Protection Officer:</strong> privacy@flashai.com<br />
                  <strong>General Inquiries:</strong> support@flashai.com<br />
                  <strong>Address:</strong> Flash AI, 123 Business Street, Suite 100, City, State 12345<br />
                  <strong>Phone:</strong> +1 (555) 123-4567
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataProtection;
